import { List, Set } from 'immutable';
import every from 'lodash/every';
import findIndex from 'lodash/findIndex';
import isNumber from 'lodash/isNumber';
import memoize from 'lodash/memoize';
import uniq from 'lodash/uniq';

import { PandoraTypes } from '../constants/SourceTypes';

import { getAnnotationsById } from './EntityUtils';

// Gets the current page based on the offset we are on.
export function getCurrentPage(offset: number, totalPages: number, limit: number): number {
  // if offset % limit is not zero, the offset is actually on the following page
  const page = Math.ceil(offset / limit) + (offset % limit === 0 ? 1 : 0);
  return page > totalPages + 1 || !isNumber(page) ? -1 : page;
}

// Given page n, offset is LIMIT * (n-1)
export function getOffsetToFetchWith(page: number, limit: number): number {
  return limit * (page - 1);
}

// Given any offset, we first calcualte what page the offset resides in.
// Then, we calculate the offset of that page based on the pageSize.
export function getPageNormalizedOffset(
  offset: number,
  totalPages: number,
  pageSize: number,
): number {
  return offset > 0
    ? getOffsetToFetchWith(getCurrentPage(offset, totalPages, pageSize), pageSize)
    : 0;
}

// Given any two offsets sIndex and eIndex, we normalized these offsets in terms of the pageSize.
// Then, return a list of offsets in terms of pageSize that are within the range of startIndex and endIndex.
export function getOffsetsInRange(
  totalTracks: number,
  pageSize: number,
  sIndex: number,
  eIndex: number,
): number[] {
  const startIndex = Math.min(sIndex, eIndex);
  const endIndex = Math.max(sIndex, eIndex);
  // normalize startIndex and endIndex
  const totalPages = Math.ceil(totalTracks / pageSize);
  const startOffset = getPageNormalizedOffset(startIndex, totalPages, pageSize);
  const endOffset = getPageNormalizedOffset(endIndex, totalPages, pageSize);
  const offsetsInRange = [];
  if (startOffset >= 0 && endOffset >= 0) {
    for (let i = startOffset; i <= endOffset; i += pageSize) {
      offsetsInRange.push(i);
    }
  }
  return offsetsInRange;
}

// Given the first/last visible indices, totalPages, and pageSize
// returns a list of visible pages, plus the pages above and below the visible range of pages
export function getPagesToFetchBasedOnIndicesInView(
  firstVisibleIndex: number,
  lastVisibleIndex: number,
  totalPages: number,
  pageSize: number,
): number[] {
  if (!every([firstVisibleIndex, lastVisibleIndex, totalPages, pageSize], isNumber)) {
    return [];
  }
  // Add pages based on indices in view
  const pagesToFetch = [];
  const firstVisiblePage = getCurrentPage(firstVisibleIndex, totalPages, pageSize);
  const lastVisiblePage = getCurrentPage(lastVisibleIndex, totalPages, pageSize);
  for (let i = firstVisiblePage - 1; i <= lastVisiblePage + 1; i++) {
    if (i > 0 && i <= totalPages) {
      pagesToFetch.push(i);
    }
  }
  return pagesToFetch;
}

// Given a list of filtered items, firstVisibleIndex, and lastVisibleIndex, get a list of items in view.
// Then, return a list of pages that the items in view are in, in terms of the original list
export function getPagesToFetchBasedOnItemsInView(
  firstVisibleIndex: number,
  lastVisibleIndex: number,
  filteredItems: $TSFixMe[],
  unfilteredItems: $TSFixMe[],
  totalPages: number,
  pageSize: number,
): number[] {
  const itemsInView = filteredItems.slice(firstVisibleIndex, lastVisibleIndex + 1);
  const pagesToFetch = itemsInView.reduce((accumulator: $TSFixMe, currentItem: $TSFixMe) => {
    const indexInUnfilteredItems = findIndex(unfilteredItems, {
      pandoraId: currentItem.pandoraId,
    });
    if (indexInUnfilteredItems >= 0) {
      const pageToFetch = getCurrentPage(indexInUnfilteredItems, totalPages, pageSize);
      if (pageToFetch >= 0) {
        accumulator.push(pageToFetch);
      }
    }
    return accumulator;
  }, []);
  return uniq(pagesToFetch);
}

// Used in the uncommon situation where we have to take the information from the grid
// and map it to actual indices of lazily loaded data.
// For example if the grid is on offsets [100, 160], but [0, 100] were preloaded
// we would want to communicate that we need to fetch starting at 0 for the lazy loaded api
export function getPlaceholderShift(
  firstVisibleIndex: number,
  lastVisibleIndex: number,
  filteredItems: $TSFixMe[],
): $TSFixMe[] {
  // We want to filter items that are neither lazy loader nor placeholders (as placeholders stand in for lazily loaded items)
  // for lazy loaded items. In the case of My Music All view that would mean filtering out all stations.
  // It is assumed that consumers of this function add properties isLazyLoaded to
  // items that should be lazily loaded.
  const filter = (item: $TSFixMe): boolean =>
    !item || !(item && (item.isLazyLoaded || item.isPlaceholder));

  return [
    filteredItems.slice(0, firstVisibleIndex).filter(filter).length,
    filteredItems.slice(0, lastVisibleIndex).filter(filter).length,
  ];
}

// Default function that decides whether a track is considered fetched
// can be overwritten by the caller of getLoadedPages
function fetchedTrackVerifier(track: $TSFixMe): boolean {
  return !!track;
}

// Function that checks to see if we have annotations to verify if a track
// is fetched for My Music grids/lists
export function fetchedAnnotationMyMusicVerifier(
  track: $TSFixMe,
  state: $TSFixMe,
  offsetFetched: $TSFixMe,
): boolean {
  return !!(track && getAnnotationsById(state, track.pandoraId) && offsetFetched);
}

export function fetchedAnnotationMyCollectionVerifier(
  track: $TSFixMe,
  state: $TSFixMe,
  offsetFetched: $TSFixMe,
): boolean {
  if (track && offsetFetched) {
    if (track.pandoraType === PandoraTypes.USER_STATION) {
      // NOTE: we are bypassing misfetch verification because we use the getStations response
      // to annotate stations, which will fetch all stations upfront.
      return true;
    }

    return !!getAnnotationsById(state, track.pandoraId);
  }

  return false;
}

// Function that checks to see if we have annotations to verify if a pandoraId
// is fetched for TrackLists on now playing and editable track list on BSP
export function fetchedAnnotationTrackListVerifier(pandoraId: $TSFixMe, state: $TSFixMe): boolean {
  return !!(pandoraId && getAnnotationsById(state, pandoraId));
}

// Function that checks to see if we have annotations to verify if a pandoraId
// is fetched for TrackLists on now playing and editable track list on BSP OR
// we check to see if the offset is fetched
export function fetchedAnnotationAndOffsetVerifier(
  pandoraId: $TSFixMe,
  state: $TSFixMe,
  offsetFetched: $TSFixMe,
): boolean {
  return !!(pandoraId && getAnnotationsById(state, pandoraId) && offsetFetched);
}

function numFetchedItems(
  items: $TSFixMe,
  state: $TSFixMe,
  verifier = fetchedTrackVerifier,
): $TSFixMe[] {
  return items
    ? items.reduce((acc: $TSFixMe, curr: $TSFixMe) => {
        // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 2.
        return verifier(curr, state) ? acc + 1 : acc;
      }, 0)
    : 0;
}

// Gets a list of loaded pages based on the items in store and pageSize.
// memoized by sourceId, pageSize, fetchedOffsets, and number of fetched items
export const getLoadedPages = memoize(
  (_, pageSize, fetchedOffsets = Set(), items = List(), verifier = fetchedTrackVerifier, state) => {
    const pagesLoaded = [];
    for (let i = 0; i < items.size; i += pageSize) {
      const page = Math.floor(i / pageSize) + 1;
      let isPageLoaded = true;
      for (let j = i; j < i + pageSize && j < items.size; j++) {
        if (!verifier(items.get(j), state, fetchedOffsets.includes(i))) {
          isPageLoaded = false;
          break;
        }
      }
      if (isPageLoaded) {
        pagesLoaded.push(page);
      }
    }
    return pagesLoaded;
  },
  (
    sourceId,
    pageSize,
    fetchedOffsets = Set(),
    items = List(),
    verifier = fetchedTrackVerifier,
    state,
  ) => {
    return sourceId + pageSize + fetchedOffsets.join(',') + numFetchedItems(items, state, verifier);
  },
);

export function getPagesToFetchWith(
  totalItems: number,
  API_ITEM_LIMIT: number,
  infiniteGridProperties: $TSFixMe,
): number {
  let firstVisibleIndex = 0;
  let lastVisibleIndex = 0;
  if (infiniteGridProperties) {
    firstVisibleIndex = infiniteGridProperties.windowingParameters.firstVisibleIndex;
    lastVisibleIndex = infiniteGridProperties.windowingParameters.lastIndex;
  }

  const totalPages = Math.ceil(totalItems / API_ITEM_LIMIT);
  let pagesToFetch: $TSFixMe = [];
  if (isNumber(firstVisibleIndex) && isNumber(lastVisibleIndex)) {
    pagesToFetch = getPagesToFetchBasedOnIndicesInView(
      firstVisibleIndex,
      lastVisibleIndex,
      totalPages,
      API_ITEM_LIMIT,
    );
  }

  return pagesToFetch;
}
